import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  FileText, 
  Kanban, 
  Users, 
  Settings, 
  LogOut,
  Crown,
  ChevronRight,
  HardHat,
  FileSignature,
  MessageSquare,
  GitCompare,
  FileDown,
  Shield,
  Building2,
  HelpCircle,
  PanelLeftClose,
  PanelLeftOpen,
  Landmark,
  TrendingUp,
  ListTree,
  UsersRound,
  Briefcase,
  Home,
  Globe,
  Calculator,
  Phone,
  CalendarDays,
  FileSearch,
  Zap,
  Command,
  Menu,
  X,
  Store,
  DollarSign,
  Network,
  Star,
  RefreshCw,
  ChevronDown
} from "lucide-react";
import { Button } from "../ui/button";
import { useSidebar } from "../../contexts/SidebarContext";
import { useState, useEffect, useCallback } from "react";
import { FounderBadge } from "../FounderBadge";
import DealForgeLogo, { DealForgeIcon } from "../DealForgeLogo";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Portal role config
const PORTAL_ROLES = {
  lender:     { label: "Lender",     icon: Landmark,  path: "/lender/portal",       color: "text-blue-400",    border: "border-blue-500/30",    bg: "bg-blue-500/10" },
  realtor:    { label: "Realtor",    icon: Home,       path: "/realtor/dashboard",   color: "text-emerald-400", border: "border-emerald-500/30", bg: "bg-emerald-500/10" },
  contractor: { label: "Contractor", icon: HardHat,    path: "/contractor/dashboard",color: "text-orange-400",  border: "border-orange-500/30",  bg: "bg-orange-500/10" },
  title:      { label: "Title Co.",  icon: Briefcase,  path: "/title/dashboard",     color: "text-purple-400",  border: "border-purple-500/30",  bg: "bg-purple-500/10" },
};

export const Sidebar = ({ user, subscription, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const tier = subscription?.tier || user?.subscription_tier || "starter";
  const isAdmin = user?.email && ["jamesdehart@dealforge.pro", "jdehart06@gmail.com", "TheDeHartInvestmentGroup@outlook.com", "admin@dealforge.com"].includes(user.email);
  
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [networkPulse, setNetworkPulse] = useState(false);
  const [switchingRole, setSwitchingRole] = useState(null);

  // Get user's extra portal roles
  const extraRoles = user?.extra_roles || [];
  const allRoles = isAdmin
    ? ["lender", "realtor", "contractor", "title"]
    : extraRoles.filter(r => PORTAL_ROLES[r]);

  const handleSwitchToPortal = useCallback(async (role) => {
    setSwitchingRole(role);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/auth/portal-token/${role}`,
        {},
        { withCredentials: true }
      );
      navigate(res.data.redirect || PORTAL_ROLES[role]?.path || "/dashboard");
    } catch (err) {
      // If not yet added, redirect to upgrade page
      navigate("/upgrade");
    } finally {
      setSwitchingRole(null);
    }
  }, [navigate]);
  
  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Deal Pulse badge — show if user hasn't visited network in 2+ hours
  useEffect(() => {
    const NETWORK_PATH = "/network";
    if (location.pathname.startsWith(NETWORK_PATH)) {
      // On network page — reset timer and clear badge
      localStorage.setItem("networkLastVisit", String(Date.now()));
      setNetworkPulse(false);
    } else {
      const lastVisit = localStorage.getItem("networkLastVisit");
      const twoHours = 2 * 60 * 60 * 1000;
      if (!lastVisit || Date.now() - parseInt(lastVisit) > twoHours) {
        setNetworkPulse(true);
      }
    }
  }, [location.pathname]);

  // Navigation sections with semantic grouping
  const navSections = [
    {
      title: null,
      items: [
        { icon: Command, label: "Command Center", path: "/dashboard" },
        { icon: TrendingUp, label: "Performance", path: "/performance", proBadge: tier === "starter" },
      ]
    },
    {
      title: "PIPELINE",
      items: [
        { icon: FileText, label: "Transactions", path: "/deals" },
        { icon: Kanban, label: "Execution Flow", path: "/execution-flow" },
        { icon: Calculator, label: "Capital Model", path: "/calculator" },
        { icon: Building2, label: "Underwriting", path: "/underwriting" },
        { icon: GitCompare, label: "Scenarios", path: "/compare" },
      ]
    },
    {
      title: "NETWORK",
      items: [
        { icon: Globe, label: "Network Hub", path: "/network", pulse: true },
        { icon: Zap, label: "Exchange", path: "/network/exchange", highlight: true },
        { icon: Star, label: "Reputation", path: "/reputation" },
        { icon: Network, label: "Network Credits", path: "/network-credits" },
        { icon: MessageSquare, label: "Messages", path: "/messages" },
      ]
    },
    {
      title: "EXECUTE",
      items: [
        { icon: CalendarDays, label: "Deal Calendar", path: "/calendar" },
        { icon: FileSignature, label: "Contracts", path: "/contracts" },
        { icon: Users, label: "Buyers", path: "/buyers" },
        { icon: FileDown, label: "Deal Packets", path: "/deal-packets" },
      ]
    },
    {
      title: "TOOLS",
      items: [
        { icon: FileSearch, label: "Doc Intelligence", path: "/document-intelligence" },
        { icon: Phone, label: "Dialer", path: "/dialer" },
        { icon: MessageSquare, label: "SMS Messaging", path: "/sms" },
        { icon: ListTree, label: "Lists", path: "/imported-lists" },
      ]
    },
    {
      title: null,
      items: [
        { icon: UsersRound, label: "Team", path: "/team" },
        { icon: DollarSign, label: "Payments", path: "/payments" },
        { icon: Settings, label: "Settings", path: "/settings" },
        { icon: HelpCircle, label: "Support", path: "/support" },
      ]
    }
  ];

  // Add admin section if applicable
  if (isAdmin) {
    navSections[navSections.length - 1].items.push(
      { icon: Shield, label: "Portals", path: "/admin/portals", adminOnly: true },
      { icon: Shield, label: "Admin", path: "/admin", adminOnly: true }
    );
  }

  return (
    <>
      {/* Mobile Header with Hamburger */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-black border-b border-white/[0.06] flex items-center justify-between px-4 z-50">
        <Link to="/dashboard" className="flex items-center">
          <DealForgeLogo size="xs" showTM={true} />
        </Link>
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 text-white/70 hover:text-white"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
      </div>
      
      {/* Mobile Slide-out Menu */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[60]">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          {/* Menu Panel — slides from LEFT */}
          <div className="absolute top-0 left-0 h-full w-[280px] bg-[#0A0A0A] border-r border-white/[0.06] overflow-y-auto flex flex-col">
            {/* Close button */}
            <div className="flex items-center justify-between p-4 border-b border-white/[0.06] flex-shrink-0">
              <DealForgeLogo size="xs" showTM={true} />
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 text-white/50 hover:text-white"
                data-testid="mobile-menu-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Founder Badge */}
            {subscription?.has_founder_badge && (
              <div className="px-4 pt-3 pb-1 flex-shrink-0">
                <FounderBadge size="sm" />
              </div>
            )}
            
            {/* Mobile Nav Items */}
            <nav className="p-3 flex-1">
              {navSections.map((section, sectionIndex) => (
                <div key={sectionIndex} className={sectionIndex > 0 ? "mt-4" : ""}>
                  {section.title && (
                    <div className="px-3 mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/25">
                        {section.title}
                      </span>
                    </div>
                  )}
                  <ul className="space-y-1">
                    {section.items.map((item) => {
                      const isActive = location.pathname === item.path;
                      return (
                        <li key={item.path}>
                          <Link
                            to={item.path}
                            onClick={() => setMobileOpen(false)}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                              isActive 
                                ? "bg-[#D4AF37]/10 text-[#D4AF37]" 
                                : "text-white/60 hover:text-white hover:bg-white/[0.04]"
                            }`}
                          >
                            <div className="relative flex-shrink-0">
                              <item.icon className="w-5 h-5" strokeWidth={1.5} />
                              {item.pulse && networkPulse && !isActive && (
                                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#D4AF37]">
                                  <span className="absolute inset-0 rounded-full bg-[#D4AF37] animate-ping opacity-75" />
                                </span>
                              )}
                            </div>
                            <span className="text-sm font-medium flex-1">{item.label}</span>
                            {item.pulse && networkPulse && !isActive && (
                              <span className="text-[8px] font-bold text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/25 px-1.5 py-0.5 rounded-full">NEW</span>
                            )}
                            {item.proBadge && !item.pulse && <Crown className="w-3 h-3 text-[#D4AF37]/60 ml-auto" />}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>

            {/* MY PORTALS — Portal Switcher */}
            {allRoles.length > 0 && (
              <div className="border-t border-white/[0.06] px-3 py-3 flex-shrink-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/25 mb-2 px-1">My Portals</p>
                <div className="space-y-1.5">
                  {allRoles.map((role) => {
                    const cfg = PORTAL_ROLES[role];
                    if (!cfg) return null;
                    const Icon = cfg.icon;
                    const isSwitching = switchingRole === role;
                    return (
                      <button
                        key={role}
                        onClick={() => { setMobileOpen(false); handleSwitchToPortal(role); }}
                        disabled={isSwitching}
                        data-testid={`mobile-switch-to-${role}-btn`}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all border ${cfg.border} ${cfg.bg}`}
                      >
                        {isSwitching
                          ? <RefreshCw className={`w-4 h-4 animate-spin ${cfg.color} flex-shrink-0`} />
                          : <Icon className={`w-4 h-4 ${cfg.color} flex-shrink-0`} />
                        }
                        <span className={`text-sm font-medium ${cfg.color}`}>{cfg.label} Portal</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Logout */}
            <div className="p-3 border-t border-white/[0.06] flex-shrink-0">
              <Button
                variant="ghost"
                onClick={() => { setMobileOpen(false); onLogout(); }}
                className="w-full justify-start gap-3 text-white/50 hover:text-white"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-sm font-medium">Sign Out</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden md:flex fixed left-0 top-0 h-screen bg-black border-r border-white/[0.04] flex-col z-40 transition-all duration-300 ease-out ${
          isCollapsed ? "w-[60px]" : "w-[240px]"
        }`}
        data-testid="sidebar"
      >
      {/* Header - Logo */}
      <div className={`h-16 flex items-center border-b border-white/[0.04] ${isCollapsed ? "justify-center px-3" : "justify-between px-4"}`}>
        {!isCollapsed ? (
          <Link to="/dashboard" className="flex items-center group">
            <DealForgeLogo size="sm" showTM={true} />
          </Link>
        ) : (
          <Link to="/dashboard" className="flex items-center justify-center">
            <DealForgeIcon className="text-xl" />
          </Link>
        )}
        {!isCollapsed && (
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1.5 text-white/40 hover:text-white hover:bg-white/[0.04] rounded-md transition-colors"
            data-testid="sidebar-toggle"
            title="Collapse sidebar"
          >
            <PanelLeftClose className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Founder Badge - shown when user has founder status */}
      {!isCollapsed && user?.has_founder_badge && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <FounderBadge show={true} size="sm" />
        </div>
      )}
      {isCollapsed && user?.has_founder_badge && (
        <div className="flex justify-center py-2 border-b border-white/[0.04]" title="DealForge Founder">
          <Shield className="w-4 h-4 text-[#D4AF37]" />
        </div>
      )}

      {/* Collapsed Toggle Button */}
      {isCollapsed && (
        <button
          onClick={() => setIsCollapsed(false)}
          className="mx-3 my-3 p-2 text-white/40 hover:text-white hover:bg-white/[0.04] rounded-md transition-colors flex justify-center"
          data-testid="sidebar-expand"
          title="Expand sidebar"
        >
          <PanelLeftOpen className="w-4 h-4" />
        </button>
      )}

      {/* Navigation */}
      <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden">
        {navSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className={sectionIndex > 0 ? "mt-5" : ""}>
            {/* Section Title */}
            {section.title && !isCollapsed && (
              <div className="px-4 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/25">
                  {section.title}
                </span>
              </div>
            )}
            {section.title && isCollapsed && (
              <div className="mx-3 my-2 border-t border-white/[0.04]" />
            )}
            
            {/* Nav Items */}
            <ul className="space-y-0.5 px-2">
              {section.items.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path} className="relative">
                    {/* Active Indicator */}
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-[#D4AF37] rounded-r-full shadow-[0_0_10px_rgba(212,175,55,0.5)]" />
                    )}
                    <Link
                      to={item.path}
                      className={`group flex items-center rounded-lg transition-all duration-200 ${
                        isCollapsed ? "justify-center p-2.5" : "gap-3 px-3 py-2"
                      } ${
                        isActive 
                          ? "bg-[#D4AF37]/10 text-[#D4AF37]" 
                          : item.highlight && !isActive
                            ? "text-[#D4AF37]/70 hover:text-[#D4AF37] hover:bg-[#D4AF37]/[0.06] border border-[#D4AF37]/15 bg-[#D4AF37]/[0.03]"
                            : "text-white/50 hover:text-white hover:bg-white/[0.04]"
                      }`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      title={isCollapsed ? item.label : undefined}
                    >
                      <div className="relative flex-shrink-0">
                        <item.icon 
                          className={`transition-transform group-hover:scale-110 ${
                            isCollapsed ? "w-5 h-5" : "w-[18px] h-[18px]"
                          } ${isActive ? "drop-shadow-[0_0_8px_rgba(212,175,55,0.5)]" : ""}`} 
                          strokeWidth={isActive ? 2 : 1.5} 
                        />
                        {item.pulse && networkPulse && !isActive && (
                          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#D4AF37] shadow-[0_0_6px_rgba(212,175,55,0.8)]">
                            <span className="absolute inset-0 rounded-full bg-[#D4AF37] animate-ping opacity-75" />
                          </span>
                        )}
                      </div>
                      {!isCollapsed && (
                        <>
                          <span className="flex-1 text-[13px] font-medium">{item.label}</span>
                          {item.pulse && networkPulse && !isActive && (
                            <span className="text-[8px] font-bold text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/25 px-1.5 py-0.5 rounded-full">
                              NEW
                            </span>
                          )}
                          {item.adminOnly && (
                            <Shield className="w-3 h-3 text-red-500/60" />
                          )}
                        </>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* MY PORTALS — Role Switcher */}
      {allRoles.length > 0 && (
        <div className={`border-t border-white/[0.04] ${isCollapsed ? "p-2" : "px-3 py-3"}`}>
          {!isCollapsed && (
            <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-white/25 mb-2 px-1">My Portals</p>
          )}
          <div className={`${isCollapsed ? "space-y-1" : "space-y-1"}`}>
            {allRoles.map((role) => {
              const cfg = PORTAL_ROLES[role];
              if (!cfg) return null;
              const Icon = cfg.icon;
              const isSwitching = switchingRole === role;
              return (
                <button
                  key={role}
                  onClick={() => handleSwitchToPortal(role)}
                  disabled={isSwitching}
                  data-testid={`switch-to-${role}-btn`}
                  title={isCollapsed ? cfg.label : undefined}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 transition-all border ${cfg.border} ${cfg.bg} rounded hover:opacity-90 ${isCollapsed ? "justify-center" : ""}`}
                >
                  {isSwitching
                    ? <RefreshCw className={`w-4 h-4 animate-spin ${cfg.color} flex-shrink-0`} />
                    : <Icon className={`w-4 h-4 ${cfg.color} flex-shrink-0`} />
                  }
                  {!isCollapsed && (
                    <span className={`text-[12px] font-medium ${cfg.color} flex-1 text-left`}>{cfg.label} Portal</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Logout */}
      <div className={`border-t border-white/[0.04] ${isCollapsed ? "p-2" : "p-3"}`}>
        <Button
          variant="ghost"
          onClick={onLogout}
          className={`text-white/40 hover:text-white hover:bg-white/[0.04] ${
            isCollapsed ? "w-full justify-center p-2.5" : "w-full justify-start gap-3 px-3 py-2 h-auto"
          }`}
          data-testid="logout-btn"
          title={isCollapsed ? "Sign Out" : undefined}
        >
          <LogOut className={`flex-shrink-0 ${isCollapsed ? "w-5 h-5" : "w-[18px] h-[18px]"}`} strokeWidth={1.5} />
          {!isCollapsed && <span className="text-[13px] font-medium">Sign Out</span>}
        </Button>
      </div>
    </aside>
    </>
  );
};
